import { useRef, useState } from 'react';
import { unmaskNumber } from '@givebutter/util';
import * as Form from 'components/form';
import { InputCurrencyIcon } from 'components/input';
import * as Select from 'components/select';
import { FileRefMeta, UploadRef, UseFormUpload } from 'components/upload';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import useDebounce from 'customHooks/useDebounce';
import { useInfiniteCampaignTeams } from 'customHooks/useInfiniteCampaignTeams';

interface MemberManualFormValues {
    displayName: string;
    goal: string;
    team: Select.SelectItemWithLabel[];
    picture: FileRefMeta[];
}

const MemberDisplayNameInput = () => {
    const { control, path } = Form.useFormContext<MemberManualFormValues>();

    return (
        <Form.Container>
            <Form.Label name={path('displayName')}>
                Display name
                <Form.LabelAsterisk />
            </Form.Label>
            <Form.TextInputRoot
                control={control}
                name={'displayName'}
                rules={{ required: 'Display name is required' }}
            >
                <Form.TextInputForm />
            </Form.TextInputRoot>
            <Form.Error name={path('displayName')} />
        </Form.Container>
    );
};

const MemberGoalInput = () => {
    const { control, path } = Form.useFormContext<MemberManualFormValues>();

    return (
        <Form.Container>
            <Form.Label name={path('goal')}>Goal</Form.Label>
            <Form.TextInputRoot control={control} name={'goal'}>
                <InputCurrencyIcon />
                <Form.TextInputForm />
            </Form.TextInputRoot>
        </Form.Container>
    );
};
const MemberTeamInput = () => {
    const { data: campaign } = useCurrentCampaign();
    const campaignId = campaign?.id!;
    const { control, path } = Form.useFormContext<MemberManualFormValues>();
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search);
    const { ref, data } = useInfiniteCampaignTeams({
        campaignId: campaignId,
        query: debouncedSearch,
    });
    const teams = data?.pages ?? [];

    if (teams.length === 0) {
        return null;
    }

    return (
        <Form.Container>
            <Form.Label name={path('team')}>Team</Form.Label>
            <Select.UseFormSearchableSelect
                control={control}
                name={'team'}
                showTags={true}
                multi={false}
                clearable={true}
                localSearch={false}
                inputProps={{
                    placeholder: 'Select existing team',
                    onChange: e => {
                        setSearch(e.currentTarget.value);
                    },
                }}
            >
                {teams.map(team => {
                    return (
                        <Select.ListItem
                            key={team.id}
                            value={{
                                id: team.id.toString(),
                                label: team.name,
                            }}
                        >
                            {team.name}
                        </Select.ListItem>
                    );
                })}
                <div ref={ref} />
            </Select.UseFormSearchableSelect>
        </Form.Container>
    );
};
const MemberPictureInput = () => {
    const { control, path } = Form.useFormContext<MemberManualFormValues>();
    const uploadRef = useRef<UploadRef>(null);

    return (
        <div className={'w-[150px]'}>
            <Form.Container>
                <Form.Label name={path('picture')}>Picture</Form.Label>
                <UseFormUpload
                    variant={'single'}
                    inputRef={uploadRef}
                    name={'picture'}
                    control={control}
                    acceptedFileTypes={['image/*']}
                    strategy={'remote'}
                    allowReorder={false}
                    allowMultiple={false}
                    allowReplace={true}
                    labelIdle={'Click or drag file'}
                    // @ts-ignore
                    allowRemove={false}
                />
            </Form.Container>
        </div>
    );
};

const transformFormValues = async (values: MemberManualFormValues) => {
    const goal = unmaskNumber(values.goal);

    return {
        display_name: values.displayName,
        goal: goal > 0 ? goal : null,
        team_id: values.team.length > 0 ? parseInt(values.team[0].id) : null,
    };
};

const DisplayName = MemberDisplayNameInput;
const Goal = MemberGoalInput;
const Picture = MemberPictureInput;
const Team = MemberTeamInput;

export {
    MemberDisplayNameInput,
    MemberGoalInput,
    MemberPictureInput,
    MemberTeamInput,
    //
    DisplayName,
    Goal,
    Picture,
    Team,
    //
    transformFormValues,
};
export type { MemberManualFormValues };
